import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FormDrawer from "../../components/layout/FormDrawer";
import { BRAND } from "../../components/auth/authTheme";
import "../../styles/attendance.css";

const legendData = [
  { code: "P", label: "Present", className: "present" },
  { code: "A", label: "Absent", className: "absent" },
  { code: "O", label: "Off Day", className: "offday" },
  { code: "R", label: "Rest Day", className: "restday" },
  { code: "PL", label: "Paid Leave", className: "leave" },
  { code: "UL", label: "Unpaid Leave", className: "unpaid_leave" },
  { code: "H", label: "Holiday", className: "holiday" },
  { code: "?", label: "Before Joining", className: "before-joining" },
];

const statusGradients = {
  A: "linear-gradient(135deg, #f8d7da 0%, #fbe9eb 100%)",
  O: "linear-gradient(135deg, #f1f8e9 0%, #f3f9ec 100%)",
  R: "linear-gradient(135deg, #e0f7fa 0%, #e9f9fb 100%)",
  PL: "linear-gradient(135deg, #e38582 0%, #f1c2c1 100%)",
  UL: "linear-gradient(135deg, #ffd699 0%, #ffebcc 100%)",
  P: "linear-gradient(135deg, #d4edda 0%, #dbf0e0 100%)",
  "?": "linear-gradient(135deg, #eeeeee 0%, #f9f9f9 100%)",
  default: "#f5f5f5",
};

const getLocalDateISO = (date) => {
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 10);
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const DRAWER_WIDTH = { xs: "100%", sm: 600, md: 1000 };

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "N/A";
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const sumAllowances = (allowances) => {
  if (!allowances || typeof allowances !== "object") return 0;
  return Object.values(allowances).reduce(
    (sum, val) => sum + Number(val || 0),
    0
  );
};

const getStatusLabel = (code) => {
  switch (code) {
    case "R":
      return "Rest Day";
    case "O":
      return "Off Day";
    case "A":
      return "Absent";
    case "P":
      return "Present";
    case "UL":
      return "Unpaid Leave";
    case "PL":
      return "Paid Leave";
    case "H":
      return "Holiday";
    case "?":
      return "Before Joining";
    default:
      return "No Attendance Record";
  }
};

const DetailItem = ({ icon, label, value, fullWidth = false, compact = false }) => (
  <Box
    sx={{
      display: "flex",
      gap: compact ? 1.25 : 2,
      p: compact ? 1.25 : 2,
      borderRadius: compact ? "10px" : "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Box
      sx={{
        width: compact ? 34 : 40,
        height: compact ? 34 : 40,
        borderRadius: compact ? "8px" : "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
        "& .MuiSvgIcon-root": { fontSize: compact ? "1rem" : "1.25rem" },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: compact ? "0.625rem" : "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color:
            value !== null && value !== undefined && value !== "N/A"
              ? BRAND.navy
              : BRAND.muted,
          fontWeight:
            value !== null && value !== undefined && value !== "N/A" ? 600 : 400,
          mt: 0.25,
          wordBreak: "break-word",
          fontSize: compact ? "0.875rem" : "1rem",
        }}
      >
        {value !== null && value !== undefined ? value : "N/A"}
      </Typography>
    </Box>
  </Box>
);

const StatTile = ({ label, value }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "10px",
      border: `1px solid ${BRAND.border}`,
      px: 1,
      py: 0.875,
      textAlign: "center",
      minWidth: 0,
    }}
  >
    <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: BRAND.navy, lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        mt: 0.25,
        lineHeight: 1.25,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const Section = ({ title, children, compact = false }) => (
  <Box sx={{ mb: compact ? 2 : 4 }}>
    <Typography
      variant="h6"
      sx={{
        mb: compact ? 1.25 : 2,
        color: BRAND.navy,
        fontWeight: 700,
        fontSize: compact ? "0.9375rem" : "1.125rem",
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const PayrollView = ({ show, handleClose, payroll }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [statusMap, setStatusMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [legendOpen, setLegendOpen] = useState(false);

  const monthDate = useMemo(() => {
    if (!payroll?.effective_from_date) return new Date();
    return new Date(payroll.effective_from_date);
  }, [payroll]);

  useEffect(() => {
    if (!payroll?.effective_from_date) return;
    setSelectedDate(new Date(payroll.effective_from_date));
  }, [payroll?.effective_from_date, show]);

  useEffect(() => {
    if (!payroll?.daily_status) return;

    const map = {};
    payroll.daily_status.forEach((entry) => {
      const code = entry.status === null ? null : entry.status;
      let detail = "No Record";

      switch (code) {
        case "A":
          detail = "Absent";
          break;
        case "P":
          detail = "Present";
          break;
        case "PL":
          detail = "Paid Leave";
          break;
        case "UL":
          detail = "Unpaid Leave";
          break;
        case "O":
          detail = "Off Day";
          break;
        case "R":
          detail = "Rest Day";
          break;
        case "H":
          detail = "Holiday";
          break;
        default:
          detail = "No Record";
      }

      map[entry.date] = { code, detail };
    });

    setStatusMap(map);
  }, [payroll]);

  const getMonthName = (date) =>
    date.toLocaleString("default", { month: "long", year: "numeric" });

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();

  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  if (!payroll) return null;

  const salary = payroll.salary || {};
  const allowancesTotal = sumAllowances(salary.other_allowances);
  const selectedCode = statusMap[getLocalDateISO(selectedDate)]?.code || "";
  const legendClass =
    legendData.find((l) => l.code === selectedCode)?.className ||
    (selectedCode === "?" ? "before-joining" : "");

  const salaryItems = [
    { icon: <AttachMoneyOutlinedIcon />, label: "Basic Salary", value: formatCurrency(salary.basic_salary) },
    { icon: <AttachMoneyOutlinedIcon />, label: "HRA", value: formatCurrency(salary.hra) },
    { icon: <AccountBalanceOutlinedIcon />, label: "Bonus", value: formatCurrency(salary.bonus) },
    { icon: <AttachMoneyOutlinedIcon />, label: "Allowances", value: formatCurrency(allowancesTotal) },
    { icon: <AttachMoneyOutlinedIcon />, label: "Deductions", value: formatCurrency(salary.total_deductions_amount) },
    { icon: <AccountBalanceOutlinedIcon />, label: "Gross Salary", value: formatCurrency(salary.gross_salary) },
    { icon: <AccountBalanceOutlinedIcon />, label: "Net Salary", value: formatCurrency(salary.net_salary) },
  ];

  const attendanceStats = [
    { label: "Working Days", value: salary.total_working_days || 0 },
    { label: "Present", value: salary.present_days || 0 },
    { label: "Absent", value: salary.absent_days || 0 },
    { label: "Payable", value: salary.payable_days || 0 },
  ];

  const renderCalendar = () => {
    const month = monthDate.getMonth();
    const year = monthDate.getFullYear();
    const effectiveFrom = new Date(payroll.effective_from_date);

    return (
      <div className="attendance-panel">
        <div className="attendance-calendar-nav">
          <h4 className="attendance-calendar-month">{getMonthName(monthDate)}</h4>
        </div>
        <div className="attendance-calendar-grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div className="attendance-calendar-weekday" key={day}>
              {isMobile ? day.charAt(0) : day}
            </div>
          ))}
          {Array(getFirstDayOfMonth(month, year))
            .fill(null)
            .map((_, i) => (
              <div key={`empty-${i}`} className="attendance-calendar-cell empty" />
            ))}
          {Array(daysInMonth(month, year))
            .fill(null)
            .map((_, i) => {
              const date = i + 1;
              const cellDate = new Date(year, month, date);
              const fullDate = getLocalDateISO(cellDate);
              let status = statusMap[fullDate];
              let code = status?.code || "";

              if (!status && cellDate < effectiveFrom) {
                code = "?";
                status = { code: "?", detail: "Before Joining" };
              }

              const cellClass =
                legendData.find((l) => l.code === code)?.className ||
                (code === "?" ? "before-joining" : "");

              return (
                <div
                  key={date}
                  className={[
                    "attendance-calendar-cell",
                    cellClass,
                    isSameDay(cellDate, selectedDate) ? "is-selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => setSelectedDate(cellDate)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setSelectedDate(cellDate);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="attendance-calendar-date">{date}</span>
                  {code && (
                    <span className={`attendance-calendar-code legend-symbol ${cellClass}`}>
                      {code}
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      title={payroll?.employee_name || "Employee"}
      subtitle={
        isMobile
          ? payroll?.employee_id || "Payroll details"
          : `Payroll details for ${payroll?.employee_id || "employee"}`
      }
    >
      <Box
        className={isMobile ? "attendance-drawer-body-mobile" : undefined}
        sx={{
          p: isMobile ? 2 : 3,
          pt: isMobile ? 0.5 : 1,
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 2 : 3,
          bgcolor: isMobile ? BRAND.bg : undefined,
        }}
      >
        {isMobile && (
          <Box
            sx={{
              bgcolor: BRAND.orangeLight,
              borderRadius: "12px",
              border: `1px solid ${BRAND.orange}33`,
              p: 1.5,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.6875rem", color: BRAND.muted, fontWeight: 600 }}>
              NET SALARY
            </Typography>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: BRAND.navy, lineHeight: 1.2 }}>
              {formatCurrency(salary.net_salary)}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: BRAND.muted, mt: 0.25 }}>
              Gross {formatCurrency(salary.gross_salary)}
            </Typography>
          </Box>
        )}

        <Section title="Salary Details" compact={isMobile}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : { xs: "1fr", sm: "1fr 1fr" },
              gap: isMobile ? 1 : 2,
            }}
          >
            {salaryItems.map((item) => (
              <DetailItem key={item.label} {...item} compact={isMobile} />
            ))}
          </Box>
        </Section>

        <Section title="Attendance Overview" compact={isMobile}>
          {isMobile ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1 }}>
              {attendanceStats.map((stat) => (
                <StatTile key={stat.label} {...stat} />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <DetailItem
                icon={<CalendarTodayOutlinedIcon />}
                label="Total Working Days"
                value={salary.total_working_days || 0}
              />
              <DetailItem
                icon={<EventAvailableOutlinedIcon />}
                label="Present Days"
                value={salary.present_days || 0}
              />
              <DetailItem
                icon={<EventBusyOutlinedIcon />}
                label="Absent Days"
                value={salary.absent_days || 0}
              />
              <DetailItem
                icon={<EventBusyOutlinedIcon />}
                label="Unpaid Leaves"
                value={salary.unpaid_leaves || 0}
              />
              <DetailItem
                icon={<CalendarTodayOutlinedIcon />}
                label="Payable Days"
                value={salary.payable_days || 0}
              />
            </Box>
          )}
        </Section>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : { xs: "1fr", md: "1fr 1fr" },
            gap: isMobile ? 1.5 : 3,
          }}
        >
          {renderCalendar()}

          <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? 1.5 : 3 }}>
            <div className="attendance-panel">
              <h5 className="attendance-panel-title">Day Details</h5>
              <div className="attendance-day-summary">
                <div className="attendance-day-date-block">
                  <div className="attendance-day-date-num">{selectedDate.getDate()}</div>
                  <div className="attendance-day-date-weekday">
                    {selectedDate.toLocaleString("en-us", { weekday: "short" })}
                  </div>
                </div>
                <div>
                  <p className="attendance-day-meta-title">
                    {getMonthName(selectedDate).split(" ")[0]} {selectedDate.getFullYear()}
                  </p>
                  <p className="attendance-day-meta-sub">
                    {statusMap[getLocalDateISO(selectedDate)]?.detail || "No record"}
                  </p>
                </div>
              </div>
              <div
                className={`attendance-status-banner ${legendClass}`}
                style={{
                  background:
                    statusGradients[selectedCode] || statusGradients.default,
                }}
              >
                <strong>{getStatusLabel(selectedCode)}</strong>
              </div>
            </div>

            <div className="attendance-panel attendance-legend-panel">
              <button
                type="button"
                className="attendance-legend-toggle"
                onClick={() => setLegendOpen((prev) => !prev)}
                aria-expanded={legendOpen}
              >
                <h5 className="attendance-panel-title">Legends</h5>
                {isMobile && (
                  <ExpandMoreIcon
                    sx={{
                      color: BRAND.muted,
                      transform: legendOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  />
                )}
              </button>
              <Collapse in={!isMobile || legendOpen}>
                <div className="attendance-legend-grid">
                  {legendData.map((item) => (
                    <div className="attendance-legend-item" key={item.code}>
                      <span className={`legend-symbol ${item.className}`}>{item.code}</span>
                      <span className="attendance-legend-label">{item.label}</span>
                    </div>
                  ))}
                </div>
              </Collapse>
            </div>
          </Box>
        </Box>
      </Box>
    </FormDrawer>
  );
};

export default PayrollView;
